import { Video, VideoOff, ChevronUp, Settings } from "lucide-react";
import { cn } from "@vc/lib/utils";
import { useTranslation } from "@/clientWeb/i18n/LanguageContext";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/clientWeb/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/clientWeb/components/ui/dropdown-menu";
import { useDeviceEnumeration, useDevicesStore } from "@/features/devices";
import { useMeetingSessionStore } from "@/features/meeting/store/meeting-session.store";
import { DeviceMenuItem } from "../common/DeviceMenuItem";

interface CameraControlProps {
  onOpenSettings?: (tab: "audio" | "video" | "advanced") => void;
  compact?: boolean;
  /** Host-controls gate from the feature layer — when false, the camera
   *  toggle is inert (video-access disabled by the host). */
  disabled?: boolean;
  /** Called instead of toggling when [disabled] — see MicControl. */
  onBlocked?: () => void;
}

export function CameraControl({ onOpenSettings, compact = false, disabled = false, onBlocked }: CameraControlProps) {
  const t = useTranslation();
  const { cams } = useDeviceEnumeration();
  const selectedCamId = useDevicesStore((s) => s.selectedCamId);
  const setSelectedCamId = useDevicesStore((s) => s.selectCam);
  const camOn = useMeetingSessionStore((s) => s.camOn);
  const onToggleCam = useMeetingSessionStore((s) => s.toggleCam);
  const canUseVideo = !disabled;

  return (
    <div className="relative">
      <div className="group flex items-center transition-all duration-300 ease-out hover:scale-105 hover:-translate-y-1 active:scale-110 overflow-hidden rounded-full bg-vc-surface-elevated">
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={canUseVideo ? onToggleCam : onBlocked}
                disabled={!canUseVideo && !onBlocked}
                className={cn(
                  "grid place-items-center transition-all duration-200 cursor-pointer",
                  compact ? "h-11 w-11" : "h-10 w-10",
                  !canUseVideo
                    ? "bg-vc-surface-elevated/30 cursor-not-allowed opacity-50"
                    : !camOn
                    ? "bg-[#ea4335]/20 ring-2 ring-[#ea4335]/40 hover:bg-[#ea4335]/30"
                    : "hover:bg-vc-surface-hover",
                )}
                aria-label={camOn ? t("controlBar.turnOffCamera") : t("controlBar.turnOnCamera")}
              >
                {camOn ? <Video className={cn("text-white", compact ? "h-4 w-4" : "h-5 w-5")} /> : <VideoOff className={cn("text-white", compact ? "h-4 w-4" : "h-5 w-5")} />}
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={8} className="border-none capitalize">
              {camOn ? t("controlBar.turnOffCamera") : t("controlBar.turnOnCamera")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {camOn && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                style={{ display: "flex" }}
                type="button"
                className={cn(
                  "flex cursor-pointer place-items-center text-white/80 transition hover:bg-vc-surface-hover",
                  compact ? "h-11 w-7" : "h-10 w-7"
                )}
                aria-label={t("controlBar.cameraDevices")}
              >
                <ChevronUp className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="start" className="min-w-[260px]">
              <div className="px-2 py-1 flex justify-between items-center text-xs font-semibold text-slate-500 dark:text-muted-foreground">
                <span>{t("controlBar.camera")}</span>
                {/* Settings Button */}
                {onOpenSettings && (
                  <TooltipProvider delayDuration={100}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => onOpenSettings("video")}
                          className="flex items-center justify-center p-1 text-slate-500 dark:text-muted-foreground hover:text-slate-700 dark:hover:text-foreground hover:bg-slate-100 dark:hover:bg-white/10 rounded transition"
                          aria-label={t("controlBar.videoSettings")}
                        >
                          <Settings className={cn("transition-transform duration-200 hover:rotate-90", "h-3.5 w-3.5")} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" sideOffset={8} className="border-none capitalize">
                        {t("controlBar.videoSettings")}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              {cams.map((d) => (
                <DeviceMenuItem
                  key={d.deviceId}
                  label={d.label || t("controlBar.camera")}
                  selected={selectedCamId === d.deviceId}
                  onSelect={() => setSelectedCamId(d.deviceId)}
                />
              ))}

            </DropdownMenuContent>

          </DropdownMenu>
        )}


      </div>
    </div>
  );
}
